<template>
  <div class="artisio-table">
    <div class="table-header">
      <div class="d-flex mb-2 justify-content-between">
        <div class="d-flex align-items-center">
          <slot name="header-buttons" v-bind:selected-items="selectedItems"></slot>
          <span class="ml-2 pr-1 border-dark" v-if="selectable" :class="{'border-right': persistentSelectedItems}">
            {{
              $t("Selected {selected} out of {total} on this page", {
                selected: selectedItems.size,
                total: items.length
              })
            }}
          </span>
          <a href="javascript:void(0)" class="ml-1" v-if="persistentSelectedItems" @click="onViewSelectionClick"
             v-b-tooltip.hover.bottomleft="$t('View Selection')">
            {{ $t('{total} total selected', {total: selectedItemsAll.size}) }}
          </a>
        </div>
        <div class="text-right d-flex align-items-center justify-content-end">
          <slot name="before-search-placeholder"></slot>
          <b-button variant="secondary" @click="onExportClick('xlsx')"
                    v-if="exportVuexModuleName && enableExport" v-permission="exportPermissionName"
                    class="shadow-none ml-2" v-b-tooltip.hover.bottomleft="$t('Export')">
            <font-awesome-icon icon="download" class="icon"/>
          </b-button>
          <b-button variant="secondary" @click="onFrontendExportClick('xlsx')"
                    :disabled="frontendExportInProgress || !items || items.length === 0"
                    v-permission="exportPermissionName" v-if="enableFrontendExport"
                    class="shadow-none ml-2" v-b-tooltip.hover.bottomleft="$t('Export')">
            <span v-if="frontendExportInProgress"
                  class="spinner-border spinner-border-sm"
                  role="status"
                  style="width: 1.1em;height: 1.1em;"
                  aria-hidden="true"></span>
            <font-awesome-icon v-else icon="download" class="icon"/>
          </b-button>
          <b-button v-if="filterToggleString" variant="secondary" v-b-toggle="filterToggleString"
                    class="shadow-none ml-2" id="filter-toggle-btn" v-b-tooltip.hover.bottomleft="$t('Filter')">
            <font-awesome-icon icon="filter" class="icon"/>
          </b-button>
          <b-button v-if="showToggleColumns" class="ml-2" @click="onToggleColumnsClick"
                    id="show-toggle-columns-modal-btn" v-b-tooltip.hover.bottomleft="$t('Toggle Columns')">
            <font-awesome-icon icon="columns" class="icon"/>
          </b-button>
          <search :initial-search="search" @input="onSearchChange" v-if="enableSimpleSearch && pagination"
                  class="ml-2"/>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12">
          <slot name="over-table-area"></slot>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12">
          <slot name="search-form-placeholder"></slot>
        </div>
      </div>
    </div>
    <div class="clearfix"></div>
    <div class="table-content border-top">
      <b-table :style="{'min-height': requiresMinHeight ? '300px': ''}"
               class="table-wrapper table-sm d-flex overflow-auto mb-0" show-empty :responsive="true" no-sort-reset
               :busy="busy" :no-local-sorting="true" :items="computedItems" @sort-changed="onSortChange"
               :fields="fields" :primary-key="primaryKey" :sort-by="sortBy" :sort-desc="sortDesc"
               :sticky-header="stickyHeader ? '100%' : false">
        <template v-slot:table-busy>
          <div class="text-center text-danger my-2">
            <spinner :show="true"/>
          </div>
        </template>
        <template v-slot:empty="scope">
          <no-data-available :height="10"/>
        </template>
        <template v-if="selectable" v-bind:slot="`head(checkbox)`" slot-scope="data">
          <b-form-checkbox @change="selectAllRows($event)" v-model="isSelectedAll"/>
        </template>
        <template v-if="selectable" v-bind:slot="`cell(checkbox)`" slot-scope="{item}">
          <b-form-checkbox style="padding-left: 2.01em" @change="onRowSelect($event, item)"
                           v-if="checkDisplayCheckbox(item)"
                           :checked="selectedItemUuids.has(item.uuid)"/>
        </template>
        <template v-if="index" v-bind:slot="`cell(index)`" slot-scope="data">
          <div class="text-center">
            {{ calculateTableIndex(data.index) }}
          </div>
        </template>
        <template v-bind:slot="slotName" slot-scope="scope" v-for="(slot, slotName) in $scopedSlots">
          <slot v-bind:name="slotName" v-bind="scope"/>
        </template>
      </b-table>
    </div>
    <div v-if="pagination" class="table-footer border-top pt-2">
      <div class="d-flex justify-content-between align-items-center" v-if="!busy">
        <showing-total-text v-if="pagination && pagination.total" :pagination="pagination"></showing-total-text>
        <per-page class="artisio-table-per-page ml-auto" :pagination="pagination" v-if="pagination"
                  :limit-variants="limitVariants"
                  @limitChange="onLimitChange"/>
        <paginator class="ml-2" :pagination="pagination" v-if="!hidePerPageSelector && pagination"
                   @pageChange="onPageChange"/>
      </div>
    </div>
    <toggle-column-modal :uuid="uuid" v-if="showToggleColumns" :extra-data="extraData" @on-column-save="onColumnSave"/>
  </div>
</template>

<script>
  import PerPage from "../paginator/PerPage";
  import Search from "../paginator/Search";
  import ShowingTotalText from "../paginator/ShowingTotalText";
  import Paginator from "../paginator/Paginator";
  import Spinner from "../common/Spinner";
  import NoDataAvailable from "../common/NoDataAvailable";
  import ExportsService, {EXPORT_TYPES} from "../../services/exports.service";
  import _ from 'lodash';
  import {createNamespacedHelpers} from "vuex";
  import ToggleColumnModal from "../toggle-columns/ToggleColumnModal";
  import ArtisioTableExportHelper from "@/core/components/ams-table/ArtisioTableExportHelper";
  import uuidV4 from "apollo11-tree/src/utils/uuidV4";

  const {mapActions} = createNamespacedHelpers('global');

  export default {
    name: "ArtisioTable",
    components: {ToggleColumnModal, NoDataAvailable, Spinner, Paginator, ShowingTotalText, Search, PerPage},
    props: {
      busy: {
        type: Boolean,
        default: false
      },
      pagination: {
        type: Object,
        required: false,
        default: null
      },
      enableFrontEndPagination: {
        type: Boolean,
        default: false
      },
      items: {
        type: Array,
        required: true
      },
      fields: {
        type: Array,
      },
      index: {
        type: Boolean,
        default: false
      },
      checkboxes: {
        type: Boolean,
        default: true
      },
      selectable: {
        type: Boolean,
        default: false
      },
      sort: {
        type: String,
        required: false
      },
      search: {
        type: String,
        required: false
      },
      enableSimpleSearch: {
        type: Boolean,
        default: true
      },
      exportVuexModuleName: {
        type: String,
        default: null
      },
      exportAttributes: {
        type: Object,
        default: () => {
        }
      },
      exportPermissionName: {
        type: String,
        default: null
      },
      enableExport: {
        type: Boolean,
        default: false
      },
      showToggleColumns: {
        type: Boolean,
        default: false,
      },
      extraData: {
        type: Object,
        default: () => {
        }
      },
      hidePerPageSelector: {
        type: Boolean,
        default: false
      },
      filterToggleString: {
        type: String,
        default: null
      },
      stickyHeader: {
        type: Boolean,
        default: true
      },
      primaryKey: {
        type: String,
        default: ''
      },
      requiresMinHeight: {
        type: Boolean,
        default: false
      },
      limitVariants: {
        type: Array,
        default: () => [5, 10, 25, 50, 200],
      },
      displayCheckbox: {
        type: Function,
        default: null
      },
      enableFrontendExport: {
        type: Boolean,
        default: false
      },
      persistentSelectedItems: {
        type: Boolean,
        default: false
      },
      persistentSelectionColumns: {
        type: Array,
        default: () => null,
        required: false
      }
    },
    data() {
      return {
        selectedItems: new Set(),
        selectedItemUuids: new Set(),
        selectedItemsAll: new Set(),
        isSelectedAll: false,
        exportTypes: EXPORT_TYPES,
        frontEndPaginationItems: [],
        frontendExportInProgress: false,
        uuid: uuidV4()
      };
    },
    mounted() {
      if (this.enableFrontEndPagination) {
        this.frontEndPaginationItems = [...this.items];
      }
    },
    computed: {
      sortBy() {
        if (!this.sort) {
          return null
        }
        return this.sort.replace(/^-?/, '');
      },
      sortDesc() {
        if (!this.sort) {
          return null;
        }
        return this.sort[0] === '-';
      },
      computedItems() {
        return this.enableFrontEndPagination ? this.itemsChunk : this.items;
      },
      itemsChunk() {
        return _.chunk(this.frontEndPaginationItems, this.pagination.limit)[this.pagination.page - 1];
      }
    },
    watch: {
      items() {
        if (this.selectable) {
          this.clearSelected();
          this.initPreviouslySelectedItems()
        }
        if (this.enableFrontEndPagination) {
          this.frontEndPaginationItems = [...this.items];
        }
      },
      fields: {
        deep: true,
        handler() {
          /* Watcher was added for the case when fields were changed after the component was mounted.
             Therefore, we need to add fields like index, checkbox etc. again after the change */
          this.addExtraFields();
        }
      }
    },
    methods: {
      ...mapActions(['showToggleColumnsModal', 'updateUserExtraData', 'showArtisioTableSelectionModal']),
      calculateTableIndex(tableIndex) {
        if (this.pagination) {
          tableIndex = tableIndex + 1;
          return ((this.pagination.page - 1) * this.pagination.limit) + tableIndex;
        }
        return tableIndex + 1;
      },
      onSearchChange(value) {
        this.clearSelected();
        this.pagination.page = 1;
        this.$emit('paramsChanged', {
          pagination: this.pagination,
          ordering: this.sort,
          search: value
        });
      },
      onLimitChange(value) {
        this.clearSelected();
        this.pagination.limit = value;
        this.pagination.page = 1;
        this.$emit('paramsChanged', {
          pagination: this.pagination,
          ordering: this.sort,
          search: this.search
        });
      },
      async onSortChange(data) {
        this.clearSelected();
        this.$emit('onSortChange', data);
        this.$emit('paramsChanged', {
          pagination: this.pagination,
          ordering: `${data.sortDesc ? '-' : ''}${data.sortBy}`,
          search: this.search
        });
        await this.updateUserExtraData({data, tableName: this.extraData.tableName});
      },
      onPageChange(page) {
        if (!this.enableFrontEndPagination) {
          this.clearSelected();
          this.pagination.page = page;
          this.$emit('paramsChanged', {
            pagination: this.pagination,
            ordering: this.sort,
            search: this.search
          });
        } else {
          this.pagination.page = page;
        }
      },
      onRowSelect(checked, item) {
        if (checked) {
          this.selectedItems.add(item)
          this.selectedItemUuids.add(item.uuid)
        } else {
          for (let i of this.selectedItems) {
            if (i.uuid === item.uuid) {
              this.selectedItems.delete(i)
              break
            }
          }
          this.selectedItemUuids.delete(item.uuid)
        }
        this.isSelectedAll = this.selectedItems.size === this.items.length;
        this.selectedItems = new Set(this.selectedItems);
        this.$emit('selectedItemsChange', this.selectedItems);

        this.handleSelectedItemsPreservation(item, checked)
      },
      selectAllRows(checked) {
        if (!checked) {
          this.handleSelectedItemsPreservation(null, false)
        }
        this.clearSelected();
        this.isSelectedAll = checked;

        if (this.isSelectedAll) {
          this.items.forEach(item => {
            if (this.checkDisplayCheckbox(item)) {
              this.selectedItems.add(item);
              this.selectedItemUuids.add(item.uuid);
            }
          });
        }
        this.selectedItems = new Set(this.selectedItems);
        this.selectedItemUuids = new Set(this.selectedItemUuids);
        this.$emit('selectedItemsChange', this.selectedItems);
        if (checked) {
          this.handleSelectedItemsPreservation(null, true)
        }
      },
      clearSelected() {
        this.selectedItems.clear();
        this.selectedItems = new Set([...this.selectedItems]);

        this.selectedItemUuids.clear();
        this.selectedItemUuids = new Set([...this.selectedItemUuids]);

        this.$emit('selectedItemsChange', this.selectedItems);
        this.isSelectedAll = false;
      },
      handleSelectedItemsPreservation(item = null, checked) {
        if (!this.persistentSelectedItems) {
          return false
        }

        if (checked) {
          const preservedUuids = new Set([...this.selectedItemsAll].map(item => item.uuid));

          if (!item) {
            // filling all items
            for (const innerItem of this.selectedItems) {
              if (!preservedUuids.has(innerItem.uuid)) {
                const clonedItem = _.cloneDeep(innerItem);
                this.selectedItemsAll.add(clonedItem);
              }
            }
          } else if (!preservedUuids.has(item.uuid)) {
            // filling single item
            this.selectedItemsAll.add(_.cloneDeep(item));
          }
        } else {
          if (!item) {
            const uuidsToRemove = new Set([...this.selectedItems].map(i => i.uuid));
            for (const innerItem of this.selectedItemsAll) {
              if (uuidsToRemove.has(innerItem.uuid)) {
                this.selectedItemsAll.delete(innerItem);
              }
            }
          } else {
            for (const innerItem of this.selectedItemsAll) {
              if (innerItem.uuid === item.uuid) {
                this.selectedItemsAll.delete(innerItem);
                break;
              }
            }
          }
        }

        this.$emit('selectedItemsAllChange', this.selectedItemsAll);
      },
      initPreviouslySelectedItems() {
        if (!this.persistentSelectedItems || this.selectedItemsAll.size === 0) {
          return false
        }

        const existingUuids = new Set(this.items.map(i => i.uuid))
        let itemsToProcess = []

        for (let item of this.selectedItemsAll) {
          if (existingUuids.has(item.uuid)) {
            itemsToProcess.push(_.cloneDeep(item))
          }
        }

        if (itemsToProcess.length === 0) {
          return false
        }

        if (itemsToProcess.length === this.items.length) {
          this.isSelectedAll = true;
        }

        itemsToProcess.forEach(item => {
          if (this.checkDisplayCheckbox(item)) {
            this.selectedItems.add(item);
            this.selectedItemUuids.add(item.uuid);
          }
        })

        // this.selectedItems = new Set(this.selectedItems);
        this.$emit('selectedItemsChange', this.selectedItems);
      },
      onViewSelectionClick() {
        this.showArtisioTableSelectionModal({
          items: this.selectedItemsAll,
          tableIdentifier: this.uuid,
          columns: this.persistentSelectionColumns
        })
      },
      onSelectionSubmit({removedItemUuids}) {
        for (const innerItem of this.selectedItemsAll) {
          if (removedItemUuids.includes(innerItem.uuid)) {
            this.selectedItemsAll.delete(innerItem);
          }
        }

        for (const innerItem of this.selectedItems) {
          if (removedItemUuids.includes(innerItem.uuid)) {
            this.selectedItems.delete(innerItem);
          }
        }

        for (const uuid of this.selectedItemUuids) {
          if (removedItemUuids.includes(uuid)) {
            this.selectedItemUuids.delete(uuid);
          }
        }
        this.selectedItems = new Set(this.selectedItems)
        this.selectedItemsAll = new Set(this.selectedItemsAll)
        this.selectedItemUuids = new Set(this.selectedItemUuids)
        this.isSelectedAll = this.selectedItems.size === this.items.length;

        this.$emit('selectedItemsAllChange', this.selectedItemsAll);
      },
      async onExportClick(type) {
        if (!this.pagination.total) {
          this.$toast('danger', this.$i18n.t('Nothing to export'));
          return;
        }
        let success = await this.$confirm(this.$i18n.t("{count} row(s) will be exported. Do you want to continue?", {count: this.pagination.total}));
        if (!success) {
          return;
        }

        const requestParams = await ExportsService.prepareRequestExportsParams(this.exportAttributes, this.exportVuexModuleName, type);
        let response = await ExportsService.scheduleExport(requestParams);
        if (response.success) {
          this.$toast('success', this.$i18n.t('Export generation has successfully started'));
        } else {
          if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0]);
          } else if (response.toastError) {
            this.$errorDialog(response.toastError);
          }
        }
      },
      addExtraFields() {
        if (this.index && !this.fields.find(f => f.key === 'index')) {
          this.fields.unshift({
            key: "index",
            label: "#",
            tdClass: 'width-2'
          });
        }
        if (this.selectable && !this.fields.find(f => f.key === 'checkbox')) {
          this.fields.unshift({
            key: "checkbox",
            label: '',
            tdClass: 'width-2',
            sortable: false
          });
        }
      },
      onToggleColumnsClick() {
        // todo remove exclude columns usage after all table exclude columns prop will be deleted
        let fields = _.cloneDeep(this.extraData.fields).filter(field => !this.extraData.excludeColumns || !this.extraData.excludeColumns.includes(field.key))

        this.showToggleColumnsModal({uuid: this.uuid, fields})
      },
      checkDisplayCheckbox(item) {
        if (!this.displayCheckbox) {
          return true;
        }
        return this.displayCheckbox(item);
      },
      async onColumnSave({selectedColumns, isSaveAction}) {
        await this.$emit('on-column-save', {selectedColumns, isSaveAction})
      },
      async onFrontendExportClick() {
        try {
          let success = await this.$confirm(this.$i18n.t('You are going to export {count} item(s). Are you sure you want to continue?', {count: this.items.length}))
          if (!success) {
            return false
          }

          this.frontendExportInProgress = true
          let data = ArtisioTableExportHelper.getExportDataByModuleName(
            this.exportVuexModuleName, this.fields, this.items, this, this.extraData.dynamicFields
          )
          this.frontendExportInProgress = false

          if (data === false) {
            this.$errorDialog(this.$i18n.t('Export is not enabled for following module. Please, contact administration.'))
            return false
          }
        } catch (e) {
          console.error(e);
          this.frontendExportInProgress = false
          this.$errorDialog(this.$i18n.t('Error occurred while exporting the data. Please, contact administration.'))
        }
      }
    },
    beforeDestroy() {
      this.$evBus.$off(`artisioTableSelectionSubmit-${this.uuid}`)
    },
    beforeMount() {
      this.addExtraFields();
      this.$evBus.$on(`artisioTableSelectionSubmit-${this.uuid}`, this.onSelectionSubmit)
    }
  }
</script>

<style lang="scss">

  @import "./../../scss/index";

  .artisio-table {
    white-space: nowrap;
    overflow-x: hidden;
    position: relative;

    .b-table {
      border-collapse: separate;
      border-spacing: 0;

      > thead {

        > tr {
          border: $ams-table-head-row-border;

          > th {
            border: $ams-table-head-cell-border;
            font-weight: $ams-table-head-cell-font-weight;
            color: $ams-table-head-cell-font-color;
            padding: 0.5rem;
            background-color: $ams-table-head-bg-color;

            &.table-b-table-default {
              background-color: $ams-table-head-bg-color;
            }

            &:focus {
              outline: $ams-table-head-cell-focus-outline;
            }
          }

          > th[aria-sort='descending'],
          > th[aria-sort='ascending'] {
            background-color: $ams-table-head-cell-sort-bg-color;
            font-weight: $ams-table-head-cell-sort-font-weight;
          }
        }
      }

      tbody {
        tr {

          td {
            &:first-child {
              padding: 0.3rem;
            }

            padding: 0.3rem 10px 0.3rem 10px;

            &.width-5 {
              width: 5em;
            }

            &.width-2 {
              width: 2rem;
            }

            &.width-5 {
              width: 5rem;
            }

            &.width-6 {
              width: 6rem;
            }

            &.width-10 {
              width: 10em;
            }

            &.width-15 {
              width: 2em;
            }

            &.width-12 {
              width: 12em;
            }

            &.thumbnail-img-holder {
              width: 5em !important;
              padding-right: 2em;
            }

            .actions-text {
              white-space: nowrap;
            }
          }
        }
      }
    }

    .per-page {
      display: inline-block;
    }

    .paginator {
      text-align: right;
    }
  }

  @supports ((position: -webkit-sticky) or (position: sticky)) {
    .b-table-sticky-header > .table.b-table > thead > tr > th {
      z-index: 40 !important;
    }
  }
</style>
